import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreditCard, Plus, Edit, Trash2, CheckCircle, XCircle, Loader2, Tag, X, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
    createDashboardUpgradeOrder,
    verifyDashboardUpgradePayment,
    getPricingPlans,
    getPublicPricingPlans,
    getPricingPlan,
    getMyProfile,
    createPricingPlan,
    updatePricingPlan,
    deletePricingPlan,
} from '../../lib/api';

const toNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const mapBackendToUi = (plan) => {
    const f = plan.features || {};

    const monthly = toNumber(plan.monthly_price);
    const yearly =
        toNumber(plan.yearly_price) ??
        (monthly != null ? monthly * 12 : null);

    const employeeLimit =
        f.employee_limit ??
        (f.max_employees === 'unlimited' ? 9999 : f.max_employees) ??
        0;

    const agentLimit =
        (f.max_agents === 'unlimited' ? 9999 : f.max_agents) ??
        0;

    const derivedActive = [];
    const derivedInactive = [];

    if (f.dashboard) derivedActive.push(f.dashboard === 'advanced' ? 'Advanced Dashboard' : 'Full Dashboard');
    if (f.commission_module) derivedActive.push('Commission Module'); else if (f.commission_module === false) derivedInactive.push('Commission Module');
    if (f.auto_commission) derivedActive.push('Auto Commission'); else if (f.auto_commission === false) derivedInactive.push('Auto Commission');
    if (f.payout_management) derivedActive.push('Payout Management'); else if (f.payout_management === false) derivedInactive.push('Payout Management');
    if (f.reports) derivedActive.push(f.reports === 'advanced' ? 'Advanced Reports' : 'Basic Reports');
    if (f.role_based_access) derivedActive.push('Role-Based Access'); else if (f.role_based_access === false) derivedInactive.push('Role-Based Access');
    if (f.api_integration) derivedActive.push('API Integration'); else if (f.api_integration === false) derivedInactive.push('API Integration');
    if (f.bank_integration) derivedActive.push('Bank Integration'); else if (f.bank_integration === false) derivedInactive.push('Bank Integration');
    if (f.white_label) derivedActive.push('White Label'); else if (f.white_label === false) derivedInactive.push('White Label');
    if (f.priority_support) derivedActive.push('Priority Support'); else if (f.priority_support === false) derivedInactive.push('Priority Support');
    if (f.dedicated_support) derivedActive.push('Dedicated Support'); else if (f.dedicated_support === false) derivedInactive.push('Dedicated Support');

    return {
        id: plan.id,
        planName: plan.name,
        monthlyPrice: monthly ?? plan.monthly_price,
        yearlyPrice: yearly,
        badge: f.badge || plan.description || '',
        employeeLimit: employeeLimit ?? 0,
        agentLimit: agentLimit ?? 0,
        features: f.active_features || derivedActive,
        inactiveFeatures: f.inactive_features || derivedInactive,
        commissionLabel: f.commission_label || 'Standard (20% WL / 80% Admin)',
        isActive: plan.is_active,
    };
};

const buildPayloadFromForm = (formData) => ({
    name: formData.planName,
    description: formData.badge || undefined,
    monthly_price: Number(formData.monthlyPrice),
    yearly_price: formData.yearlyPrice ? Number(formData.yearlyPrice) : null,
    is_active: formData.isActive,
    features: {
        badge: formData.badge,
        employee_limit: Number(formData.employeeLimit) || 0,
        max_agents: Number(formData.agentLimit) || 0,
        commission_label: formData.commissionLabel,
        active_features: formData.features || [],
        inactive_features: formData.inactiveFeatures || [],
    },
});

const formatPlanPrice = (value) => {
    if (value === null || value === undefined || value === '') return 'Custom';
    return Number(value);
};

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (document.getElementById('razorpay-script')) {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.id = 'razorpay-script';
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

const PricingPlanCards = ({
    plans,
    navigate,
    actionRenderer,
    currentPlanId = null,
    billingCycle = 'monthly',
}) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-5">
        {plans.map((plan) => {
            const isCurrentPlan = currentPlanId && String(plan.id) === String(currentPlanId);
            const isYearly = billingCycle === 'yearly';
            const primaryPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const secondaryPrice = isYearly ? plan.monthlyPrice : plan.yearlyPrice;
            const primaryLabel = isYearly ? '/ Year' : '/ Month';
            const secondaryLabel = isYearly ? '/ Month' : '/ Yearly';
            return (
                <div key={plan.id} className="rounded-xl border bg-card text-card-foreground shadow-sm flex flex-col overflow-visible relative">
                    {!plan.isActive && (
                        <div className="absolute top-0 right-0 bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded-bl-lg rounded-tr-xl z-10">
                            Inactive
                        </div>
                    )}
                    {isCurrentPlan && (
                        <div className="absolute top-0 left-0 bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-br-lg rounded-tl-xl z-10">
                            Current Plan
                        </div>
                    )}
                    <div className="p-6 pb-4 border-b bg-muted/30 relative rounded-t-xl">
                        {plan.badge && (
                            <div className="absolute -top-4 inset-x-0 flex justify-center z-10">
                                <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400 text-xs font-bold px-4 py-1.5 rounded-full border border-purple-200 shadow-sm">
                                    {plan.badge}
                                </span>
                            </div>
                        )}
                        <h3 className="text-2xl font-bold mt-4 text-center text-purple-700 dark:text-purple-400">{plan.planName}</h3>
                        <div className="mt-4 flex flex-col items-center justify-center">
                            <div className="flex items-end gap-1">
                                <span className="text-3xl font-bold">₹{formatPlanPrice(primaryPrice)}</span>
                                <span className="text-muted-foreground mb-1 text-sm font-semibold">{primaryLabel}</span>
                            </div>
                            <div className="text-sm text-yellow-600 dark:text-yellow-500 mt-1 font-bold">
                                ₹{formatPlanPrice(secondaryPrice)} {secondaryLabel}
                            </div>
                        </div>
                    </div>
                    <div className="p-6 flex-1 bg-background">
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                                <span className="text-sm font-medium">{plan.employeeLimit === 9999 ? 'Unlimited' : plan.employeeLimit} Employees</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                                <span className="text-sm font-medium">{plan.agentLimit === 9999 ? 'Unlimited' : plan.agentLimit} Agents</span>
                            </li>
                            {plan.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                                    <span className="text-sm font-medium">{f}</span>
                                </li>
                            ))}
                            {plan.inactiveFeatures && plan.inactiveFeatures.map((f, i) => (
                                <li key={`inactive-${i}`} className="flex items-start gap-3 opacity-60">
                                    <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                                    <span className="text-sm font-medium text-muted-foreground line-through">{f}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-6 pt-4 border-t border-dashed">
                            <p className="text-xs text-muted-foreground">Commission</p>
                            <p className="text-sm font-semibold">{plan.commissionLabel}</p>
                        </div>
                    </div>
                    <div className="p-4 border-t bg-muted/20 flex gap-2 rounded-b-xl">
                        {actionRenderer(plan, isCurrentPlan, navigate)}
                    </div>
                </div>
            );
        })}
    </div>
);

export const AllPricingPlans = () => {
    const [plans, setPlans] = useState([]);
    const [deleteId, setDeleteId] = useState(null);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { token } = useAuth();

    useEffect(() => {
        let isMounted = true;
        async function load() {
            try {
                const resp = await getPricingPlans(token);
                const data = resp.data || [];
                if (!isMounted) return;
                setPlans(data.map(mapBackendToUi));
            } catch (err) {
                toast({
                    title: "Failed to load pricing plans",
                    description: err.message,
                    variant: "destructive",
                });
            }
        }
        load();
        return () => { isMounted = false; };
    }, [token, toast]);

    const handleDelete = async (id) => {
        try {
            await deletePricingPlan(token, id);
            setPlans(plans.filter((p) => p.id !== id));
            setDeleteId(null);
            toast({
                title: "Plan deleted successfully",
                variant: "default",
            });
        } catch (err) {
            toast({
                title: "Failed to delete plan",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CreditCard className="h-6 w-6 text-purple-500" />
                    <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Pricing Plans</h1>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                    onClick={() => navigate('/admin/plans/create')} size="sm">
                    <Plus className="h-4 w-4" />
                    Add Plan
                </Button>
            </div>

            <PricingPlanCards
                plans={plans}
                navigate={navigate}
                billingCycle="monthly"
                actionRenderer={(plan) => (
                    <>
                        <Button variant="outline" className="flex-1 gap-2" onClick={() => navigate(`/admin/plans/edit/${plan.id}`)}>
                            <Edit className="h-4 w-4" /> Edit
                        </Button>
                        <Button variant="outline" className="flex-1 gap-2 bg-red-50 hover:bg-red-100 text-red-600 border-red-200" onClick={() => setDeleteId(plan.id)}>
                            <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                    </>
                )}
            />

            {deleteId && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-background rounded-xl p-6 max-w-sm w-full shadow-lg border">
                        <h3 className="text-lg font-bold">Delete Plan</h3>
                        <p className="text-muted-foreground mt-2">Are you sure you want to delete this pricing plan? This action cannot be undone.</p>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
                            <Button variant="destructive" onClick={() => handleDelete(deleteId)}>Delete</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const UpgradePlansPage = () => {
    const [plans, setPlans] = useState([]);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [processingPlanId, setProcessingPlanId] = useState(null);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusTone, setStatusTone] = useState('neutral');
    const navigate = useNavigate();
    const { toast } = useToast();
    const { user, token, updateProfile } = useAuth();

    useEffect(() => {
        let isMounted = true;
        async function load() {
            try {
                const resp = await getPublicPricingPlans();
                const data = Array.isArray(resp?.data) ? resp.data : [];
                if (!isMounted) return;
                setPlans(data.map(mapBackendToUi));
            } catch (err) {
                toast({
                    title: "Failed to load pricing plans",
                    description: err.message,
                    variant: "destructive",
                });
            }
        }
        load();
        return () => { isMounted = false; };
    }, [toast]);

    const currentPlanId = user?.workspace?.pricing_plan_id || null;
    const handleUpgrade = async (plan) => {
        if (!token) {
            toast({
                title: 'Please login again',
                description: 'Your session expired. Please login and retry.',
                variant: 'destructive',
            });
            return;
        }

        setProcessingPlanId(plan.id);
        setStatusMessage('');
        setStatusTone('neutral');

        try {
            const loaded = await loadRazorpayScript();
            if (!loaded) {
                throw new Error('Failed to load payment gateway. Check your internet connection.');
            }

            const orderResp = await createDashboardUpgradeOrder(token, {
                plan_id: plan.id,
                billing_cycle: billingCycle,
            });
            if (!orderResp?.success || !orderResp?.data?.order_id) {
                throw new Error('Could not initiate payment. Please try again.');
            }

            const { order_id, amount, currency, key_id } = orderResp.data;
            const options = {
                key: key_id,
                amount: String(amount),
                currency: currency || 'INR',
                name: 'FinMatrixRQ',
                description: `${plan.planName} — ${billingCycle} upgrade`,
                order_id,
                prefill: {
                    name: user?.full_name || '',
                    email: user?.email || '',
                    contact: user?.phone || '',
                },
                theme: { color: '#7c3aed' },
                modal: {
                    ondismiss: () => {
                        setStatusTone('neutral');
                        setStatusMessage('Payment was cancelled. You are still on your current plan.');
                        setProcessingPlanId(null);
                    },
                },
                handler: async (response) => {
                    try {
                        const verifyResp = await verifyDashboardUpgradePayment(
                            token,
                            response.razorpay_order_id,
                            response.razorpay_payment_id,
                            response.razorpay_signature,
                        );
                        const meResp = await getMyProfile(token);
                        if (meResp?.data) updateProfile(meResp.data);
                        setStatusTone('success');
                        setStatusMessage(
                            verifyResp?.data?.email_sent
                                ? 'Plan upgraded successfully. A confirmation email has been sent.'
                                : 'Plan upgraded successfully. Confirmation email could not be sent yet.'
                        );
                    } catch (err) {
                        setStatusTone('error');
                        setStatusMessage(err?.message || 'Payment succeeded but verification failed. Please contact support.');
                    } finally {
                        setProcessingPlanId(null);
                    }
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (response) => {
                setStatusTone('error');
                setStatusMessage(response?.error?.description || 'Payment failed. Please try again.');
                setProcessingPlanId(null);
            });
            rzp.open();
        } catch (err) {
            setStatusTone('error');
            setStatusMessage(err?.message || 'Unable to start upgrade payment. Please try again.');
            setProcessingPlanId(null);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CreditCard className="h-6 w-6 text-purple-500" />
                    <div>
                        <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Upgrade Plan</h1>
                        <p className="text-sm text-muted-foreground">Choose a plan to expand your team and feature limits.</p>
                    </div>
                </div>
                <div className="inline-flex rounded-lg border bg-muted/30 p-1">
                    {['monthly', 'yearly'].map((cycle) => (
                        <button
                            key={cycle}
                            type="button"
                            onClick={() => setBillingCycle(cycle)}
                            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${billingCycle === cycle
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-muted-foreground'
                                }`}
                        >
                            {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                        </button>
                    ))}
                </div>
            </div>

            {statusMessage ? (
                <div className={`rounded-xl border px-4 py-3 text-sm font-medium ${statusTone === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : statusTone === 'error'
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}>
                    {statusMessage}
                </div>
            ) : null}

            <PricingPlanCards
                plans={plans}
                navigate={navigate}
                currentPlanId={currentPlanId}
                billingCycle={billingCycle}
                actionRenderer={(plan, isCurrentPlan) => (
                    <Button
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white gap-2 disabled:opacity-100"
                        disabled={Boolean(isCurrentPlan) || processingPlanId === plan.id}
                        onClick={() => handleUpgrade(plan)}
                    >
                        {processingPlanId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                        {isCurrentPlan ? 'Current Plan' : processingPlanId === plan.id ? 'Processing...' : `Upgrade ${billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}`}
                    </Button>
                )}
            />
        </div>
    );
};

export const PlanForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { token } = useAuth();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        planName: '',
        monthlyPrice: '',
        yearlyPrice: '',
        badge: '',
        employeeLimit: '',
        agentLimit: '',
        commissionLabel: 'Standard (20% WL / 80% Admin)',
        isActive: true,
        features: [],
        inactiveFeatures: []
    });
    const [featureInput, setFeatureInput] = useState('');
    const [inactiveFeatureInput, setInactiveFeatureInput] = useState('');
    const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
    const [editingInactiveFeatureIndex, setEditingInactiveFeatureIndex] = useState(null);

    useEffect(() => {
        let isMounted = true;
        async function load() {
            if (!isEdit) return;
            try {
                const resp = await getPricingPlan(token, id);
                const plan = mapBackendToUi(resp.data);
                if (!isMounted) return;
                setFormData(plan);
            } catch (err) {
                toast({
                    title: "Failed to load plan",
                    description: err.message,
                    variant: "destructive",
                });
            }
        }
        load();
        return () => { isMounted = false; };
    }, [id, isEdit, token, toast]);

    const handleFeatureAdd = () => {
        if (featureInput.trim()) {
            setFormData(p => ({ ...p, features: [...p.features, featureInput.trim()] }));
            setFeatureInput('');
        }
    };

    const handleFeatureRemove = (idx) => {
        setFormData(p => ({
            ...p,
            features: p.features.filter((_, i) => i !== idx)
        }));
        setEditingFeatureIndex((current) => (current === idx ? null : current));
    };

    const handleFeatureUpdate = (idx, value) => {
        setFormData((p) => ({
            ...p,
            features: p.features.map((item, i) => (i === idx ? value : item)),
        }));
    };

    const handleInactiveFeatureAdd = () => {
        if (inactiveFeatureInput.trim()) {
            setFormData(p => ({
                ...p,
                inactiveFeatures: [...(p.inactiveFeatures || []), inactiveFeatureInput.trim()]
            }));
            setInactiveFeatureInput('');
        }
    };

    const handleInactiveFeatureRemove = (idx) => {
        setFormData(p => ({
            ...p,
            inactiveFeatures: (p.inactiveFeatures || []).filter((_, i) => i !== idx)
        }));
        setEditingInactiveFeatureIndex((current) => (current === idx ? null : current));
    };

    const handleInactiveFeatureUpdate = (idx, value) => {
        setFormData((p) => ({
            ...p,
            inactiveFeatures: (p.inactiveFeatures || []).map((item, i) => (i === idx ? value : item)),
        }));
    };

    const handleSubmit = async () => {
        if (!formData.planName || !formData.monthlyPrice || !formData.yearlyPrice) {
            toast({
                title: "Missing fields",
                description: "Please fill all required fields.",
                variant: "destructive"
            });
            return;
        }

        const payload = buildPayloadFromForm(formData);

        try {
            if (isEdit) {
                await updatePricingPlan(token, id, payload);
                toast({ title: "Plan updated successfully", variant: "default" });
            } else {
                await createPricingPlan(token, payload);
                toast({ title: "Plan created successfully", variant: "default" });
            }
            navigate('/admin/plans');
        } catch (err) {
            toast({
                title: "Failed to save plan",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    return (
        <div className="space-y-5 max-w-4xl">
            <div className="flex items-center gap-2">
                <Tag className="h-6 w-6 text-purple-500" />
                <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">
                    {isEdit ? 'Edit Pricing Plan' : 'Create Pricing Plan'}
                </h1>
            </div>

            <div className="rounded-xl border p-6 shadow-sm space-y-6 bg-card text-card-foreground">
                <h2 className="font-semibold text-base border-b pb-3">Plan Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Plan Name *</label>
                        <Input
                            placeholder="e.g. CORE PRO"
                            value={formData.planName}
                            onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Badge (Optional)</label>
                        <Input
                            placeholder="e.g. Most Popular"
                            value={formData.badge}
                            onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Monthly Price (₹) *</label>
                        <Input
                            type="number"
                            placeholder="e.g. 1499"
                            value={formData.monthlyPrice}
                            onChange={(e) => setFormData({ ...formData, monthlyPrice: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Yearly Price (₹) *</label>
                        <Input
                            type="number"
                            placeholder="e.g. 17988"
                            value={formData.yearlyPrice}
                            onChange={(e) => setFormData({ ...formData, yearlyPrice: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Employee Limit *</label>
                        <Input
                            type="number"
                            placeholder="e.g. 10 (use 9999 for unlimited)"
                            value={formData.employeeLimit}
                            onChange={(e) => setFormData({ ...formData, employeeLimit: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Agent Limit *</label>
                        <Input
                            type="number"
                            placeholder="e.g. 100 (use 9999 for unlimited)"
                            value={formData.agentLimit}
                            onChange={(e) => setFormData({ ...formData, agentLimit: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Commission Plan Preset</label>
                        <select
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={formData.commissionLabel}
                            onChange={(e) => setFormData({ ...formData, commissionLabel: e.target.value })}
                        >
                            <option>Premium (30% WL / 70% Admin)</option>
                            <option>Standard (20% WL / 80% Admin)</option>
                            <option>Basic (10% WL / 90% Admin)</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-xl bg-accent/20">
                    <div className="space-y-0.5">
                        <label className="text-base font-semibold">Plan Status</label>
                        <p className="text-sm text-muted-foreground">Toggle to make this plan active or hidden.</p>
                    </div>
                    <Switch
                        checked={formData.isActive}
                        onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Active Features List */}
                    <div className="space-y-4">
                        <h2 className="font-semibold text-base border-b pb-3 text-emerald-600">Included Features</h2>
                        <div className="flex gap-2">
                            <Input
                                placeholder="e.g. Advanced Dashboard"
                                value={featureInput}
                                onChange={(e) => setFeatureInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleFeatureAdd();
                                    }
                                }}
                            />
                            <Button type="button" onClick={handleFeatureAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                                Add
                            </Button>
                        </div>

                        {formData.features.length > 0 && (
                            <div className="bg-muted/30 p-4 rounded-lg border space-y-2">
                                {formData.features.map((f, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-background border px-3 py-2 rounded-md border-l-4 border-l-emerald-500">
                                        {editingFeatureIndex === idx ? (
                                            <Input
                                                value={f}
                                                onChange={(e) => handleFeatureUpdate(idx, e.target.value)}
                                                className="h-8 border-0 bg-transparent px-0 text-sm font-medium shadow-none focus-visible:ring-0"
                                                autoFocus
                                            />
                                        ) : (
                                            <span className="text-sm font-medium">{f}</span>
                                        )}
                                        <div className="flex items-center gap-2">
                                            {editingFeatureIndex === idx ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingFeatureIndex(null)}
                                                    className="text-muted-foreground hover:text-emerald-600 transition-colors"
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingFeatureIndex(idx)}
                                                    className="text-muted-foreground hover:text-emerald-600 transition-colors"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleFeatureRemove(idx)}
                                                className="text-muted-foreground hover:text-red-500 transition-colors"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Inactive Features List */}
                    <div className="space-y-4">
                        <h2 className="font-semibold text-base border-b pb-3 text-red-500">Excluded/Crossed Features</h2>
                        <div className="flex gap-2">
                            <Input
                                placeholder="e.g. API Integration"
                                value={inactiveFeatureInput}
                                onChange={(e) => setInactiveFeatureInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleInactiveFeatureAdd();
                                    }
                                }}
                            />
                            <Button type="button" onClick={handleInactiveFeatureAdd} className="bg-red-600 hover:bg-red-700 text-white shrink-0">
                                Add
                            </Button>
                        </div>

                        {formData.inactiveFeatures && formData.inactiveFeatures.length > 0 && (
                            <div className="bg-muted/30 p-4 rounded-lg border space-y-2">
                                {formData.inactiveFeatures.map((f, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-background border px-3 py-2 rounded-md border-l-4 border-l-red-400 opacity-80">
                                        {editingInactiveFeatureIndex === idx ? (
                                            <Input
                                                value={f}
                                                onChange={(e) => handleInactiveFeatureUpdate(idx, e.target.value)}
                                                className="h-8 border-0 bg-transparent px-0 text-sm font-medium line-through shadow-none focus-visible:ring-0"
                                                autoFocus
                                            />
                                        ) : (
                                            <span className="text-sm font-medium line-through">{f}</span>
                                        )}
                                        <div className="flex items-center gap-2">
                                            {editingInactiveFeatureIndex === idx ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingInactiveFeatureIndex(null)}
                                                    className="text-muted-foreground hover:text-emerald-600 transition-colors"
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingInactiveFeatureIndex(idx)}
                                                    className="text-muted-foreground hover:text-emerald-600 transition-colors"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleInactiveFeatureRemove(idx)}
                                                className="text-muted-foreground hover:text-red-500 transition-colors"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex gap-3 pt-6 border-t mt-8">
                    <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700 text-white">
                        {isEdit ? 'Update Plan' : 'Save Plan'}
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/admin/plans')}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};
